export enum EntityType {
  DATA_ASSET = "data_asset",
  EXPECTATION = "expectation",
  VALIDATION_RESULT = "validation_result",
  DATA_SOURCE = "data_source",
  JOB = "job",
}

export enum ActionType {
  CREATED = "created",
  UPDATED = "updated",
  DELETED = "deleted",
}

/**
 * Type representing a valid event type string
 * Example: "data_asset.created"
 */
export type EventType = `${EntityType}.${ActionType}`

/**
 * Generate an event type string from entity and action types
 *
 * @param entityType - The entity type
 * @param actionType - The action type
 * @returns A properly formatted event type string
 *
 * @example
 * ```ts
 * const eventType = createEventType(EntityType.DATA_ASSET, ActionType.UPDATED);
 * // eventType = "data_asset.updated"
 * ```
 */
export function createEventType(entityType: EntityType, actionType: ActionType): EventType {
  return `${entityType}.${actionType}`
}

/**
 * Object containing all possible event types for each entity
 */
export const EventTypes = {
  DATA_ASSET: {
    CREATED: createEventType(EntityType.DATA_ASSET, ActionType.CREATED),
    UPDATED: createEventType(EntityType.DATA_ASSET, ActionType.UPDATED),
    DELETED: createEventType(EntityType.DATA_ASSET, ActionType.DELETED),
  },
  EXPECTATION: {
    CREATED: createEventType(EntityType.EXPECTATION, ActionType.CREATED),
    UPDATED: createEventType(EntityType.EXPECTATION, ActionType.UPDATED),
    DELETED: createEventType(EntityType.EXPECTATION, ActionType.DELETED),
  },
  VALIDATION_RESULT: {
    CREATED: createEventType(EntityType.VALIDATION_RESULT, ActionType.CREATED),
    UPDATED: createEventType(EntityType.VALIDATION_RESULT, ActionType.UPDATED),
    DELETED: createEventType(EntityType.VALIDATION_RESULT, ActionType.DELETED),
  },
  DATA_SOURCE: {
    CREATED: createEventType(EntityType.DATA_SOURCE, ActionType.CREATED),
    UPDATED: createEventType(EntityType.DATA_SOURCE, ActionType.UPDATED),
    DELETED: createEventType(EntityType.DATA_SOURCE, ActionType.DELETED),
  },
  JOB: {
    CREATED: createEventType(EntityType.JOB, ActionType.CREATED),
    UPDATED: createEventType(EntityType.JOB, ActionType.UPDATED),
    DELETED: createEventType(EntityType.JOB, ActionType.DELETED),
  },
} as const
